import type { Field, RefName, Row } from "./types";

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

/** Coluna que o backend devolve com o nome do registro referenciado. */
const REF_LABEL: Record<RefName, string> = {
  colaboradores: "colaborador_nome",
  obras: "obra_nome",
  equipamentos: "equipamento_nome",
  veiculos: "veiculo_nome",
};

/**
 * Como um registro de referência aparece no select. Quase todos têm `nome`; o
 * veículo é identificado pela PLACA (com o modelo ao lado, que é como as
 * pessoas se referem a ele no pátio).
 */
export function rotuloRef(item: Row): string {
  if (item.nome) return String(item.nome);
  const partes = [item.placa, item.modelo].map((v) => String(v ?? "").trim()).filter(Boolean);
  return partes.length > 0 ? partes.join(" — ") : `#${item.id}`;
}

export function formatCurrency(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isNaN(n) ? "—" : brl.format(n);
}

export function formatDate(value: unknown): string {
  if (!value) return "—";
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

/** Formata o valor de uma célula conforme o tipo do campo. */
export function formatCell(field: Field, row: Row): string {
  if (field.ref) {
    return row[REF_LABEL[field.ref]] ?? "—";
  }
  const value = row[field.name];
  if (value === null || value === undefined || value === "") return "—";
  switch (field.type) {
    case "currency":
      return formatCurrency(value);
    case "date":
      return formatDate(value);
    case "password":
      return "••••••";
    // Competências marcadas: na tabela mostra as 2 primeiras + "+N" para não
    // estourar a coluna (a lista completa aparece ao editar).
    case "skills": {
      const itens = String(value)
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (itens.length <= 2) return itens.join(", ");
      return `${itens.slice(0, 2).join(", ")} +${itens.length - 2}`;
    }
    default:
      return String(value);
  }
}

/** Classe de cor (pílula) para um valor de status, conforme o texto. */
export function statusTone(value: string): string {
  const v = value.toLowerCase();
  if (
    // "baixado": equipamento de ensaio que saiu do uso de vez (coluna "Onde está").
    ["vencid", "atrasad", "bloqueado", "baixado", "cancelad", "insatisfeito", "aberto", "pendente", "grave", "óbito", "obito", "zerado", "abaixo do"].some((s) =>
      v.includes(s),
    )
  )
    return "danger";
  if (
    // "em uso" (equipamento preso a uma OS aberta) é aviso, não erro: o
    // equipamento está bem, apenas não está livre para outra OS. "faltante"
    // (saiu e não voltou) também é aviso: alguém precisa procurar.
    ["a vencer", "a reciclar", "vence hoje", "revisão próxima", "revisao proxima", "em andamento", "em manutenção", "em uso", "em correção", "em calibração", "em tratamento", "em separação", "em separacao", "agendada", "agendado", "planejada", "neutro", "afastado", "registrado", "moderado", "parado", "faltante"].some(
      (s) => v.includes(s),
    )
  )
    return "warn";
  if (
    // "em estoque": o equipamento de ensaio está na prateleira, pronto para sair.
    // Fora (em serviço, com cliente, em laboratório externo) fica neutro.
    ["válid", "valid", "em dia", "no prazo", "ativo", "ativa", "em atividade", "disponível", "disponivel", "autorizado", "liberado", "devolvido", "concluíd", "concluid", "executada", "resolvido", "satisfeito", "encerrado", "leve", "sem lesão", "sem lesao", "pago", "em estoque"].some(
      (s) => v.includes(s),
    )
  )
    return "ok";
  return "neutral";
}
